import { IPage } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import moment from 'moment';
import { Reducer } from 'redux';
import * as AudienceValidation from '../components/audience/AudienceValidation';
import Constants from '../constants';
import * as AudienceDetailsActions from '../containers/audience/AudienceDetailsActions';
import { ActionTypes as AudienceActionTypes } from '../containers/audience/AudienceDetailsActions';
import contentConstants from '../contentConstants';
import {
	AudiencePropertiesUnderValidation,
	AudienceStatus,
	IAudience,
	IDistributionContact,
	IImportRetryModel,
	IParticipationCountsVm,
	ISurveyAdminSummary,
	ISurveyPreviewModel,
	surveyPreviewTemplate,
	SurveyStatusType,
	SurveyStep,
} from '../models';
import { SurveyCompletionStatusType } from '../models/SurveyCompletionStatusType';
import * as AudienceAPI from '../services/AudienceAPI';
import { defaultAudiencePageRange } from '../services/AudienceAPI';
import * as ImportRetryAPI from '../services/ImportRetryAPI';
import * as ParticipantsAPI from '../services/ParticipantsAPI';
import * as SurveyAPI from '../services/SurveyAPI';
import { IApplicationState } from '../store';
import { FileDownloadHelper } from '../utilities';
import * as Actions from './Actions';
import { IParticipationMonitorCountsChanged } from './Actions';
import { ActionTypes } from './ActionTypes';
import { ActionCreators as SurveyBuilderActions } from './SurveyBuilderStore';

export interface ISurveyAdminState {
	surveyAdminSummary?: ISurveyAdminSummary;
	surveyAdminSummaryLoading: boolean;
	benchmarkIds: number[];
	surveyBenchmarksLoading: boolean;
	surveyBenchmarksSaving: boolean;
	isLoadingAudiences: boolean;
	audiences: IAudience[];
	surveyPreview: ISurveyPreviewModel;
	isSurveyPreviewLoading: boolean;
	isSurveyStatusCancelling: boolean;
	participationCounts?: IParticipationCountsVm;
	isLoadingParticipationCounts: boolean;
	isSendingRequest: boolean;
	importRetryModel: IImportRetryModel;
	pendingParticipants: IDistributionContact[];
	isLoadingPendingParticipants: boolean;
}

export const defaultState: ISurveyAdminState = {
	surveyAdminSummaryLoading: false,
	benchmarkIds: [],
	surveyBenchmarksLoading: false,
	surveyBenchmarksSaving: false,
	isLoadingAudiences: false,
	isLoadingParticipationCounts: false,
	audiences: [
		{
			audienceId: 0,
			audienceName: '',
			clientId: 0,
			surveyId: 0,
			openImmediately: false,
			audienceStatus: AudienceStatus.Incomplete,
			isTestAudience: false,
		},
	],
	surveyPreview: surveyPreviewTemplate,
	isSurveyPreviewLoading: false,
	isSurveyStatusCancelling: false,
	isSendingRequest: false,
	importRetryModel: {
		mSuiteDatasetId: 0,
	},
	pendingParticipants: [],
	isLoadingPendingParticipants: false,
};

type GetSurveyAdminSummaryActions =
	| Actions.ISurveyAdminSummaryGetRequested
	| Actions.ISurveyAdminSummaryGetSucceeded
	| Actions.ISurveyAdminSummaryGetFailed
	| Actions.INotificationWarningCustom;
type GetBenchmarksActions = Actions.ISurveyBenchmarksGetRequested | Actions.ISurveyBenchmarksGetSucceeded;
type SaveBenchmarksActions = Actions.ISurveyBenchmarksSaveRequested | Actions.ISurveyBenchmarksSaveSucceeded;
type GetAudiencesActions =
	| Actions.ISurveyAdminAudiencesGetRequested
	| Actions.ISurveyAdminAudiencesGetSucceeded
	| AudienceDetailsActions.IAudienceUpdateRequested
	| AudienceDetailsActions.IAudienceUpdateSucceeded
	| AudienceDetailsActions.IAudienceUpdateFailed
	| Actions.ISurveyAdminAudienceOpenClose
	| Actions.INotificationDismissAll;
type GetSurveyPreviewActions = Actions.ISurveyPreviewGetRequested | Actions.ISurveyPreviewGetSucceeded | Actions.ISurveyPreviewCompleted;
type GetParticipationCountsActions = Actions.IAudienceParticipationCountsGetRequested | Actions.IAudienceParticipationCountsGetSucceeded;
type RestartDatasetImportActions =
	| Actions.IRestartDatasetImportGetRequested
	| Actions.IRestartDatasetImportGetSucceeded
	| Actions.IRestartDatasetImportmSuiteDatasetIdChanged;
type GetCancelSurveyActions = Actions.ISurveyCancelRequested | Actions.ISurveyCancelSucceeded | Actions.ISurveyCancelFailed;

export const ActionCreators = {
	GetSurveyAdminSummary: (surveyId: number) => (dispatch: (action: GetSurveyAdminSummaryActions | GetSurveyPreviewActions) => void) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_SUMMARY_GET_REQUESTED });
		return SurveyAPI.getSurveyAdminSummaryViewModel(surveyId)
			.then((result: ISurveyAdminSummary) => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_SUMMARY_GET_SUCCEEDED, payload: result });
				return SurveyAPI.getSurveyPreviewModel(surveyId).then((preview: any) => {
					dispatch({ type: ActionTypes.SURVEY_PREVIEW_GET_SUCCEEDED, payload: preview });
				});
			})
			.catch(() => {
				// Show error toast
				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: contentConstants.TOAST_ERROR_SURVEY_SUMMARIES_NOT_FETCHED,
						messageTitle: contentConstants.TOAST_ERROR_TITLE_SURVEY_SUMMARIES_NOT_FETCHED,
					},
				});

				// turn off loading state
				dispatch({
					type: ActionTypes.SURVEY_ADMIN_SUMMARY_GET_FAILED,
				});
			});
	},
	GetAudiences: (surveyId: number) => (dispatch: (action: GetAudiencesActions) => void) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_AUDIENCES_GET_REQUESTED });
		return AudienceAPI.getPagedAudiences(surveyId, defaultAudiencePageRange).then((pagedResult: IPage<IAudience>) => {
			dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
			dispatch({
				type: ActionTypes.SURVEY_ADMIN_AUDIENCES_GET_SUCCEEDED,
				payload: pagedResult.items.length !== 0 ? pagedResult.items : defaultState.audiences,
			});
		});
	},
	GetParticipationCounts: (surveyId: number, audienceId: number) => (dispatch: (action: GetParticipationCountsActions) => void) => {
		dispatch({ type: ActionTypes.AUDIENCE_PARTICIPATION_COUNTS_GET_REQUESTED });
		return ParticipantsAPI.getParticipationCountsForAudience(surveyId, audienceId).then((counts: IParticipationCountsVm) => {
			dispatch({
				type: ActionTypes.AUDIENCE_PARTICIPATION_COUNTS_GET_SUCCEEDED,
				payload: counts,
			});
		});
	},
	RestartDatasetImport: (surveyId: number, mSuiteDatasetId: number) => (dispatch: (action: RestartDatasetImportActions) => void) => {
		dispatch({ type: ActionTypes.RESTART_DATASET_IMPORT_REQUESTED });
		return ImportRetryAPI.RestartDatasetImport(surveyId, mSuiteDatasetId).then(() => {
			dispatch({
				type: ActionTypes.RESTART_DATASET_IMPORT_SUCCEEDED,
			});
		});
	},
	HandlemSuiteClientGuidChange: (value: React.ChangeEvent<HTMLInputElement>) => (dispatch: any) => {
		dispatch({ type: ActionTypes.RESTART_DATASET_IMPORT_MSUITE_CLIENT_GUID_CHANGED, payload: value.target.value });
	},
	HandlemSuiteDatasetIdChange: (value: React.ChangeEvent<HTMLInputElement>) => (dispatch: any) => {
		dispatch({ type: ActionTypes.RESTART_DATASET_IMPORT_MSUITE_DATASET_ID_CHANGED, payload: value.target.value });
	},
	HandlemResearchEvtAssgIdChange: (value: React.ChangeEvent<HTMLInputElement>) => (dispatch: any) => {
		dispatch({ type: ActionTypes.RESTART_DATASET_IMPORT_MRESEARCH_EVTASSGID_CHANGED, payload: value.target.value });
	},
	HandleOpenCloseDatesChange: (audienceId: number, openImmediately: boolean, openOn?: string, closeOn?: string) => ({
		type: ActionTypes.SURVEY_ADMIN_OPEN_CLOSE_DATE_UPDATE,
		payload: {
			audienceId,
			openOn,
			closeOn,
			openImmediately,
		},
	}),
	UpdateAudience: (audience: IAudience, isReopeningSurvey: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_REQUESTED });

		const audienceToUpdate = _.find(getState().surveyAdmin.audiences, (a) => a.audienceId === audience?.audienceId);
		if (audienceToUpdate === undefined) {
			return dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_FAILED, payload: { audience: audienceToUpdate } });
		}

		const audienceValidationFields = [AudiencePropertiesUnderValidation.CloseOn];
		if (!isReopeningSurvey) {
			audienceValidationFields.push(AudiencePropertiesUnderValidation.OpenOn);
			audienceValidationFields.push(AudiencePropertiesUnderValidation.OpenCloseDates);
		}
		const audienceValidationErrors = AudienceValidation.validAudienceFields(audienceValidationFields, audience, audience.isOpen);
		if (!audienceValidationErrors) {
			return AudienceAPI.updateAudience(audience)
				.then((savedAudience: any) => {
					dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_SUCCEEDED, payload: { audience: savedAudience } });
					dispatch(ActionCreators.GetSurveyAdminSummary(audience.surveyId));
					dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
					if (isReopeningSurvey) {
						const surveyId = getState().surveyAdmin.surveyAdminSummary?.surveyId;
						if (surveyId) {
							dispatch(SurveyBuilderActions.OpenSurvey(surveyId, SurveyStep.Admin));
						}
					}
				})
				.catch((ex: any) => {
					let validationErrors;
					if (ex.response && ex.response.data && ex.response.data.validationErrors) {
						validationErrors = ex.response.data.validationErrors.validations;
					}
					audience = { ...audience, validationErrors };
					dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_FAILED, payload: { audience } });
					dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				});
		} else {
			audience = { ...audience, validationErrors: audienceValidationErrors };
			return dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_FAILED, payload: { audience } });
		}
	},
	UpdateAudiences: (audiences: IAudience[], isReopeningSurvey: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		const audienceValidationFields = [AudiencePropertiesUnderValidation.CloseOn];
		if (!isReopeningSurvey) {
			audienceValidationFields.push(AudiencePropertiesUnderValidation.OpenOn);
			audienceValidationFields.push(AudiencePropertiesUnderValidation.OpenCloseDates);
		}
		const validationErrors = audiences.map((a: IAudience) => AudienceValidation.validAudienceFields(audienceValidationFields, a, a.isOpen));
		const areAudiencesValid = validationErrors
			.map((v) => (v === undefined ? true : v.IsValid))
			.reduce((isValid, currentValue) => isValid && currentValue, true);
		if (areAudiencesValid) {
			const updateAudiencePromises: Promise<IAudience>[] = [];
			audiences.forEach((a) => updateAudiencePromises.push(AudienceAPI.updateAudience(a)));
			Promise.all(updateAudiencePromises)
				.then((savedAudiences: IAudience[]) => {
					savedAudiences.forEach((a) => {
						dispatch({ type: AudienceActionTypes.AUDIENCE_UPDATE_SUCCEEDED, payload: { audience: a } });
					});
				})
				.catch((ex: any) => {
					dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				})
				.finally(() => {
					dispatch(ActionCreators.GetSurveyAdminSummary(audiences[0].surveyId));
					if (isReopeningSurvey) {
						const surveyId = getState().surveyAdmin.surveyAdminSummary?.surveyId;
						if (surveyId) {
							dispatch(SurveyBuilderActions.OpenSurvey(surveyId, SurveyStep.Admin));
						}
					}
				});
		} else {
			dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
		}
	},
	CancelSurvey: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_CANCEL_REQUESTED });
		return SurveyAPI.UpdateSurveyStatus(surveyId, SurveyStatusType.Cancelled)
			.then(() => {
				dispatch({ type: ActionTypes.SURVEY_CANCEL_SUCCEEDED });
				dispatch(ActionCreators.GetSurveyAdminSummary(surveyId));
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			})
			.catch(() => {
				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: contentConstants.TOAST_ERROR_CANCEL_FAILURE_BODY,
						messageTitle: contentConstants.TOAST_ERROR_CANCEL_FAILURE_TITLE,
					},
				});
				dispatch({ type: ActionTypes.SURVEY_CANCEL_FAILED });
			});
	},
	DownloadSurveyURLs: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

		return SurveyAPI.downloadGroupBasedLinks(surveyId)
			.then((response) => {
				FileDownloadHelper.download(response, Constants.SurveyURLsFileName);
				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
			})
			.catch(() => {
				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: contentConstants.TOAST_SURVEY_PARTICIPANT_ACTION_FAILURE_BODY,
						messageTitle: contentConstants.TOAST_ERROR_GENERIC_TITLE,
					},
				});
			});
	},
};

type KnownActions =
	| GetSurveyAdminSummaryActions
	| GetBenchmarksActions
	| SaveBenchmarksActions
	| GetAudiencesActions
	| GetSurveyPreviewActions
	| GetParticipationCountsActions
	| RestartDatasetImportActions
	| GetCancelSurveyActions
	| IParticipationMonitorCountsChanged;

export const reducer: Reducer<ISurveyAdminState, KnownActions> = (state: ISurveyAdminState = defaultState, action: KnownActions) => {
	switch (action.type) {
		case ActionTypes.SURVEY_ADMIN_SUMMARY_GET_REQUESTED:
			return { ...state, surveyAdminSummaryLoading: true };
		case ActionTypes.SURVEY_ADMIN_SUMMARY_GET_SUCCEEDED:
			return { ...state, surveyAdminSummary: action.payload, surveyAdminSummaryLoading: false };
		case ActionTypes.SURVEY_ADMIN_SUMMARY_GET_FAILED:
			return { ...state, surveyAdminSummaryLoading: false };
		case ActionTypes.SURVEY_BENCHMARKS_GET_REQUESTED:
			return { ...state, surveyBenchmarksLoading: true };
		case ActionTypes.SURVEY_BENCHMARKS_GET_SUCCEEDED:
			return { ...state, benchmarkIds: action.payload, surveyBenchmarksLoading: false };
		case ActionTypes.SURVEY_BENCHMARKS_SAVE_REQUESTED:
			return { ...state, surveyBenchmarksSaving: true };
		case ActionTypes.SURVEY_BENCHMARKS_SAVE_SUCCEEDED:
			return { ...state, benchmarkIds: action.payload, surveyBenchmarksSaving: false };
		case ActionTypes.SURVEY_ADMIN_AUDIENCES_GET_REQUESTED:
			return { ...state, isLoadingAudiences: true };
		case ActionTypes.SURVEY_ADMIN_AUDIENCES_GET_SUCCEEDED:
			return {
				...state,
				isLoadingAudiences: false,
				audiences: action.payload,
			};
		case ActionTypes.RESTART_DATASET_IMPORT_REQUESTED:
			return { ...state, importRetryModel: { ...state.importRetryModel }, isSendingRequest: true };
		case ActionTypes.RESTART_DATASET_IMPORT_SUCCEEDED:
			return { ...state, importRetryModel: { ...state.importRetryModel } };
		case ActionTypes.RESTART_DATASET_IMPORT_MSUITE_DATASET_ID_CHANGED:
			return { ...state, importRetryModel: { ...state.importRetryModel, mSuiteDatasetId: action.payload } };
		case ActionTypes.SURVEY_PREVIEW_GET_REQUESTED:
			return { ...state, isSurveyPreviewLoading: true };
		case ActionTypes.SURVEY_PREVIEW_GET_SUCCEEDED:
			return { ...state, surveyPreview: action.payload, isSurveyPreviewLoading: false };
		case ActionTypes.AUDIENCE_PARTICIPATION_COUNTS_GET_REQUESTED:
			return { ...state, isLoadingParticipationCounts: true };
		case ActionTypes.AUDIENCE_PARTICIPATION_COUNTS_GET_SUCCEEDED:
			return { ...state, isLoadingParticipationCounts: false, participationCounts: action.payload };
		case ActionTypes.SURVEY_PREVIEW_COMPLETED:
			return { ...state };

		// Audience Updates
		case AudienceActionTypes.AUDIENCE_UPDATE_REQUESTED:
			return { ...state, isLoadingAudiences: true };
		case AudienceActionTypes.AUDIENCE_UPDATE_SUCCEEDED:
		case AudienceActionTypes.AUDIENCE_UPDATE_FAILED: {
			const updatedAudiences: IAudience[] = _.cloneDeep(state.audiences).map((a) =>
				a.audienceId === action.payload.audience.audienceId
					? { ...action.payload.audience, isClosed: moment(action.payload.audience.closeOn).isBefore(moment()) }
					: a,
			);
			return {
				...state,
				isLoadingAudiences: false,
				audiences: updatedAudiences,
			};
		}
		case ActionTypes.SURVEY_ADMIN_OPEN_CLOSE_DATE_UPDATE: {
			const updatedOpenCloseAudiences: IAudience[] = _.cloneDeep(state.audiences).map((a) =>
				a.audienceId === action.payload.audienceId ? { ...a, ...action.payload } : a,
			);
			return { ...state, audiences: updatedOpenCloseAudiences };
		}
		case ActionTypes.SURVEY_CANCEL_REQUESTED:
			return { ...state, isSurveyStatusCancelling: true };
		case ActionTypes.SURVEY_CANCEL_SUCCEEDED:
			return { ...state, isSurveyStatusCancelling: false };
		case ActionTypes.SURVEY_CANCEL_FAILED:
			return { ...state, isSurveyStatusCancelling: false };
		case ActionTypes.SIGNALR_PARTICPANT_MONITOR_COUNTS_CHANGED:
			if (state.participationCounts !== undefined && state.surveyAdminSummary?.surveyId === action.payload.surveyId) {
				const participationCounts: IParticipationCountsVm = {
					partialCompleteCount: state.participationCounts?.partialCompleteCount ?? 0,
					completeCount: state.participationCounts?.completeCount ?? 0,
					generationDate: state.participationCounts?.generationDate ?? new Date(),
					invitedCount: state.participationCounts?.invitedCount,
				};
				if (action.payload.surveyCompletionStatus === SurveyCompletionStatusType.InProgress) {
					participationCounts.partialCompleteCount++;
				} else if (action.payload.surveyCompletionStatus === SurveyCompletionStatusType.Complete) {
					participationCounts.completeCount++;
					participationCounts.partialCompleteCount--;
				}
				return { ...state, participationCounts };
			}
	}
	return state;
};
